import { PlusOutlined } from '@ant-design/icons';

import { Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';

import dayjs from 'dayjs';

import { useCustomer } from '@hooks';

import { InfoTableHelper, WrapperPage } from '@components/atoms';
import { GenericList, PageHeader } from '@components/molecules';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatCurrency, maskCPF } from '@helpers';

enum OrderEnum {
	ascend = 'asc',
	descend = 'desc',
}

const columns: ColumnsType = [
	{
		title: 'Client',
		key: 'customer',
		render: (customer: any) => (
			<InfoTableHelper
				title={customer?.name || '-'}
				subTitle={maskCPF(customer?.cpf)}
			/>
		),
		sorter: true,
	},
	{
		title: 'Marital status',
		key: 'maritalStat; aus',
		render: () => (
			<InfoTableHelper
				title={''}
				subTitle={''}
			/>
		),
		sorter: true,
	},
	{
		title: 'Office / Advisor',
		key: 'office',
		render: (customer: any) => (
			<InfoTableHelper
				title={customer?.office?.name || '-'}
				subTitle={customer?.advisor?.name || '-'}
			/>
		),
		sorter: true,
	},
	{
		title: 'Assets',
		dataIndex: 'patrimonyTotal',
		render: (patrimonyTotal: number) =>
			`${formatCurrency(patrimonyTotal ?? 0)}`,
	},
	{
		title: 'Last updated',
		key: 'updated',
		render: (customer: any) =>
			customer?.updatedAt
				? dayjs(customer.updatedAt).format('MM/DD/YYYY - HH:mm')
				: '-',
		sorter: true,
	},
];

export const Customer = () => {
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [search, setSearch] = useState('');
	const [sortField, setSortField] = useState<string | undefined>('updated');
	const [sortOrder, setSortOrder] = useState<string | undefined>('descend');
	const { data, isLoading, refetch, isRefetching } = useCustomer({
		page,
		limit,
		search,
		orderBy: OrderEnum[sortOrder as keyof typeof OrderEnum],
		orderType: sortField,
	});
	const navigate = useNavigate();
	const handleSearch = (value: string) => {
		setSearch(value);
		setPage(1);
	};

	const handleTableChange = (
		pagination: any,
		_filters: unknown,
		sorter: any
	) => {
		const { current, pageSize } = pagination;

		if (pageSize !== limit) {
			setLimit(pageSize);
			setPage(1);
			return;
		}

		if (current !== page) {
			setPage(current);
			return;
		}

		if (sorter.columnKey !== sortField || sorter.order !== sortOrder) {
			setSortField(sorter.columnKey as string);
			setSortOrder(sorter.order as string);
			setPage(1);
		}
	};

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			refetch();
		}, 300);

		return () => clearTimeout(timeoutId);
	}, [page, limit, search, sortOrder, sortField]);

	return (
		<WrapperPage isLoading={isLoading || isRefetching}>
			<PageHeader title="Clients">
				<Button
					icon={<PlusOutlined />}
					type='primary'
					size='large'
					href='/clientes/novo'
				>
					Add client
				</Button>
			</PageHeader>
			<GenericList
				columns={columns}
				dataSource={data?.data}
				onRow={(record) => ({
					onClick: () => navigate(`/clientes/${record.id}`),
				})}
				onChange={handleTableChange}
				onSearch={handleSearch}
				pagination={{
					current: data?.pagination.currentPage,
					pageSize: limit,
					total: data?.pagination.totalCount,
					onChange(page, pageSize) {
						setPage(page);
						setLimit(pageSize);
					},
					showSizeChanger: true,
				}}
			/>
		</WrapperPage>
	);
};
